import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataManager } from '@syncfusion/ej2-data';
import { Predicate, Query } from '@syncfusion/ej2-data';
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Inject,
  Page,
  Sort,
} from '@syncfusion/ej2-react-grids';
import {
  MERCHANT_GRID_COLUMNS,
  MERCHANTS_GRID_PAGE_SIZE,
  getMerchantRowId,
} from '../../../config/merchantsGrid';
import { Spinner } from '../../../shared/components/Spinner';

const MERCHANT_SEARCH_DEBOUNCE_MS = 320;

export interface MerchantsGridProps {
  dataManager: DataManager;
  onGridError: (message: string | null) => void;
  onEmptyChange: (isEmpty: boolean) => void;
}

export function MerchantsGrid({
  dataManager,
  onGridError,
  onEmptyChange,
}: MerchantsGridProps): React.ReactElement {
  const navigate = useNavigate();
  const gridRef = useRef<GridComponent>(null);
  const searchDebounceRef = useRef<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  /**
   * Remote grid calls `dataManager.executeQuery(gridQuery)` with an explicit query, so
   * `DataManager.setDefaultQuery` is ignored. Filters must live on the grid's `query` (see Syncfusion `generateQuery`).
   */
  const applySearchQuery = useCallback((trimmed: string) => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }
    if (trimmed) {
      const predicate = new Predicate('Email', 'contains', trimmed, true)
        .or('FirstName', 'contains', trimmed, true)
        .or('LastName', 'contains', trimmed, true)
        .or('Merchant.Name', 'contains', trimmed, true);
      grid.query = new Query().where(predicate);
    } else {
      grid.query = new Query();
    }
    grid.pageSettings.currentPage = 1;
    grid.refresh();
  }, []);

  useEffect(
    () => () => {
      if (searchDebounceRef.current !== undefined) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = undefined;
      }
    },
    []
  );

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchText(value);
      if (searchDebounceRef.current !== undefined) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = window.setTimeout(() => {
        searchDebounceRef.current = undefined;
        applySearchQuery(value.trim());
      }, MERCHANT_SEARCH_DEBOUNCE_MS);
    },
    [applySearchQuery]
  );

  const rowSelected = useCallback(
    (args: { data?: Record<string, unknown> }) => {
      const row = args.data;
      if (!row) {
        return;
      }
      const id = getMerchantRowId(row);
      if (!id) {
        return;
      }
      const path = `/merchants/${encodeURIComponent(id)}`;
      // Defer navigation so Syncfusion Grid can finish internal DOM work before unmount
      // (avoids React `insertBefore` / reconciliation errors with React Router).
      queueMicrotask(() => {
        navigate(path);
      });
    },
    [navigate]
  );

  const actionBegin = useCallback(() => {
    onGridError(null);
    setLoading(true);
  }, [onGridError]);

  const updateEmptyFromGrid = useCallback(() => {
    const grid = gridRef.current;
    const records = grid?.getCurrentViewRecords?.() ?? [];
    onEmptyChange(Array.isArray(records) && records.length === 0);
  }, [onEmptyChange]);

  const actionComplete = useCallback(
    (args: { error?: unknown }) => {
      setLoading(false);
      if (args?.error !== undefined && args?.error !== null) {
        onGridError(
          typeof args.error === 'string'
            ? args.error
            : 'Failed to load grid data.'
        );
      }
      updateEmptyFromGrid();
    },
    [onGridError, updateEmptyFromGrid]
  );

  const actionFailure = useCallback(
    (args: { error?: Error | string }) => {
      setLoading(false);
      const raw = args?.error;
      const msg =
        (typeof raw === 'object' && raw && 'message' in raw && raw.message) ||
        raw ||
        'The Merchants data could not be loaded. Check your connection, sign-in, and API permissions.';
      onGridError(String(msg));
    },
    [onGridError]
  );

  const dataBound = useCallback(() => {
    updateEmptyFromGrid();
  }, [updateEmptyFromGrid]);

  const columns = useMemo(
    () =>
      MERCHANT_GRID_COLUMNS.map((col) => (
        <ColumnDirective
          key={String(col.field)}
          field={col.field as string}
          headerText={col.headerText}
          width={col.width}
          isPrimaryKey={col.isPrimaryKey}
          type={col.type}
          visible={col.visible ?? true}
        />
      )),
    []
  );

  return (
    <div className="bo-grid-wrap bo-card">
      <div
        className="bo-field"
        style={{ marginBottom: '0.75rem', maxWidth: '26rem' }}
      >
        <label className="bo-field-label" htmlFor="merchants-grid-search">
          Search merchants
        </label>
        <input
          id="merchants-grid-search"
          type="search"
          className="bo-input"
          value={searchText}
          onChange={onSearchChange}
          placeholder="Email, name, or company…"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {loading ? (
        <div className="bo-grid-overlay" aria-hidden>
          <Spinner label="Loading merchants…" />
        </div>
      ) : null}
      <GridComponent
        ref={gridRef}
        dataSource={dataManager}
        allowPaging
        allowSorting
        allowFiltering
        pageSettings={{
          pageSize: MERCHANTS_GRID_PAGE_SIZE,
          pageSizes: [10, 15, 25, 50],
        }}
        filterSettings={{ type: 'Menu' }}
        width="100%"
        height={480}
        rowSelected={rowSelected}
        actionBegin={actionBegin}
        actionComplete={actionComplete}
        actionFailure={actionFailure}
        dataBound={dataBound}
      >
        <ColumnsDirective>{columns}</ColumnsDirective>
        <Inject services={[Page, Sort, Filter]} />
      </GridComponent>
    </div>
  );
}
